import { MainApi } from 'api/MainApi'
import { StationApi } from 'api/StationApi'
import clsx from 'clsx'
import { ReactComponent as ChargingSvg } from 'images/icons/plug_in.svg'
import { ReactComponent as DebrisDumpSvg } from 'images/icons/debris_dump.svg'
import { ReactComponent as ParkingSvg } from 'images/icons/parking.svg'
import { ReactComponent as RefillingSvg } from 'images/icons/refilling.svg'
import { ReactComponent as SandClockSvg } from 'images/icons/sand_clock.svg'
import { ReactComponent as WashingSvg } from 'images/icons/washing.svg'
import { ReactComponent as WasteWaterDrainSvg } from 'images/icons/waste_water_drain.svg'
import {
  formatOccupiedOrRobot,
  LotLiveTelemetry,
  parseLotTelemetryRow,
} from 'pages/Assets/stations/stationLotOccupancy'
import { FunctionComponent, SVGProps, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectLots, selectStation } from 'store/selectors/station'
import { TIMEOUT_FETCHING_STATUS } from 'utils/constants'
import { ILot } from 'utils/interfaces'
import { humanizeSnakeCaseLabel, statusTypeMap } from 'utils/lotInterfaces'
import { ACTION_TYPE_LABELS, ActionType, type ILotModelConfig } from 'utils/servicePointsCatalog'
import { formatStationErrorMsg } from 'utils/stationErrorMsg'

import style from './StationLotsTable.module.css'

const ACTION_ICONS: Partial<Record<ActionType, FunctionComponent<SVGProps<SVGSVGElement>>>> = {
  [ActionType.WAITING]: SandClockSvg,
  [ActionType.PARKING]: ParkingSvg,
  [ActionType.WASHING]: WashingSvg,
  [ActionType.CHARGING]: ChargingSvg,
  [ActionType.REFILLING]: RefillingSvg,
  [ActionType.WASTE_WATER_DRAIN]: WasteWaterDrainSvg,
  [ActionType.DEBRIS_DUMP]: DebrisDumpSvg,
}

function getLatestState(lot: ILot) {
  return lot.state_history?.[0] ?? null
}

function formatStatusCell(liveStatus: string | undefined, fallbackStatus?: number | null) {
  if (liveStatus && liveStatus !== '—') {
    return liveStatus
  }
  if (fallbackStatus != null) {
    if (fallbackStatus === 0) {
      return '—'
    }
    return statusTypeMap[fallbackStatus] ?? humanizeSnakeCaseLabel(String(fallbackStatus))
  }
  return '—'
}

/** Tooltip for Status cell: error details only (do not repeat the visible status label). */
function formatStatusTitle(liveErrorMsg?: string | null, historyErrorMsg?: string | null) {
  const formatted = formatStationErrorMsg(liveErrorMsg || historyErrorMsg)
  return formatted || undefined
}

function lotAllowedActions(lot: ILot, lotModelsByNumber: Map<string, ILotModelConfig>): ActionType[] {
  const modelNumber = lot.lot_model?.trim()
  if (!modelNumber) {
    return []
  }
  const config = lotModelsByNumber.get(modelNumber)
  return (config?.allowed_actions ?? []).filter(action => action !== ActionType.NOT_SET)
}

const StationLotsTable = () => {
  const station = useSelector(selectStation)
  const lots = useSelector(selectLots)
    .filter(lot => lot.robot_station === station.id)
    .sort((a, b) => a.lot_id - b.lot_id)
  const [liveByLotId, setLiveByLotId] = useState<Record<number, LotLiveTelemetry>>({})
  const [lotModels, setLotModels] = useState<ILotModelConfig[]>([])

  useEffect(() => {
    let cancelled = false
    MainApi.fetchLotModels()
      .then(models => {
        if (!cancelled) {
          setLotModels(Array.isArray(models) ? models : [])
        }
      })
      .catch(error => {
        console.warn('[StationLotsTable] fetchLotModels failed', error)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const lotModelsByNumber = useMemo(() => {
    const map = new Map<string, ILotModelConfig>()
    for (const model of lotModels) {
      if (model.model_number) {
        map.set(model.model_number, model)
      }
    }
    return map
  }, [lotModels])

  useEffect(() => {
    let cancelled = false

    const loadLiveTelemetry = async () => {
      try {
        const response = await StationApi.fetchTelemetrySnapshot(station.id)
        const next: Record<number, LotLiveTelemetry> = {}
        for (const lot of response.snapshot?.lots ?? []) {
          next[lot.lot.id] = parseLotTelemetryRow(lot)
        }
        if (!cancelled) {
          setLiveByLotId(next)
        }
      } catch (error) {
        console.warn('[StationLotsTable] fetchTelemetrySnapshot failed', error)
      }
    }

    loadLiveTelemetry()
    const timer = window.setInterval(loadLiveTelemetry, TIMEOUT_FETCHING_STATUS)
    return () => {
      cancelled = true
      window.clearInterval(timer)
    }
  }, [station.id])

  if (lots.length === 0) {
    return <div className={style.empty}>No lots configured for this station.</div>
  }

  return (
    <div className={style.root}>
      <div className={style.tableScrollArea}>
        <div className={style.tableScrollContent}>
          <div className={clsx(style.tableRow, style.tableHead)}>
            <div className={clsx(style.tableCol, style.lotIdCol)}>Lot</div>
            <div className={clsx(style.tableCol, style.actionsCol)}>Actions</div>
            <div className={clsx(style.tableCol, style.occupiedCol)}>Occupied</div>
            <div className={clsx(style.tableCol, style.stageCol)}>Stage</div>
            <div className={clsx(style.tableCol, style.statusCol)}>Status</div>
          </div>
          {lots.map(lot => {
            const latest = getLatestState(lot)
            const live = liveByLotId[lot.lot_id]
            const actions = lotAllowedActions(lot, lotModelsByNumber)
            const actionsTitle = actions.length
              ? actions.map(action => ACTION_TYPE_LABELS[action] ?? String(action)).join(', ')
              : '—'
            const statusLabel = formatStatusCell(live?.statusLabel, latest?.state_status)
            const statusTitle = formatStatusTitle(live?.statusErrorMsg, latest?.error_msg)
            return (
              <div key={lot.id} className={style.tableRow}>
                <div className={clsx(style.tableCol, style.lotIdCol)}>{lot.lot_id}</div>
                <div className={clsx(style.tableCol, style.actionsCol)} title={actionsTitle}>
                  {actions.length ? (
                    <span className={style.actionIcons} aria-label={actionsTitle}>
                      {actions.map(action => {
                        const ActionIcon = ACTION_ICONS[action]
                        const label = ACTION_TYPE_LABELS[action] ?? ActionType[action] ?? String(action)
                        if (!ActionIcon) {
                          return (
                            <span key={action} className={style.actionIconFallback} title={label}>
                              {label}
                            </span>
                          )
                        }
                        return (
                          <span key={action} className={style.actionIconWrap} title={label}>
                            <ActionIcon className={style.actionIcon} aria-hidden />
                            <span className={style.srOnly}>{label}</span>
                          </span>
                        )
                      })}
                    </span>
                  ) : (
                    '—'
                  )}
                </div>
                <div className={clsx(style.tableCol, style.occupiedCol)}>
                  {formatOccupiedOrRobot(live, latest)}
                </div>
                <div className={clsx(style.tableCol, style.stageCol)}>{live?.maintenanceStageLabel ?? '—'}</div>
                <div className={clsx(style.tableCol, style.statusCol)} title={statusTitle}>
                  {statusLabel}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default StationLotsTable
